import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Globe, Mail, Bot, Users, ArrowRight, CheckCircle,
  Cloud, Building2, Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

const webFeatures = [
  "Custom Design & Development",
  "E-commerce Solutions",
  "CMS Implementation",
  "Mobile-First Responsive Design",
  "SEO Optimization",
  "Analytics Setup"
];

const emailFeatures = [
  "Microsoft 365 Setup",
  "Professional Email Addresses",
  "Calendar & Contacts Sync",
  "OneDrive Cloud Storage",
  "Teams Collaboration",
  "Admin Management"
];

const managedFeatures = [
  "Custom AI Agent Development",
  "Workflow Automation",
  "Chatbot & Virtual Assistants",
  "AI Receptionist",
  "AI-Powered Analytics",
  "Seamless System Integration"
];

const consultingFeatures = [
  "IT Strategy Planning",
  "Software Selection",
  "Technology Assessment",
  "Vendor Management",
  "Cost Optimization",
  "ISO27001 Documentation Support"
];

function FeatureList({ features }) {
  return (
    <div className="grid grid-cols-2 gap-4 mb-8">
      {features.map((feature) => (
        <div key={feature} className="flex items-start gap-2">
          <CheckCircle size={18} className="text-cyan-500 mt-0.5 flex-shrink-0" />
          <span className="text-gray-300 text-sm">{feature}</span>
        </div>
      ))}
    </div>
  );
}

function DeploymentCard({ Icon, title, description, benefits }) {
  return (
    <div className="p-8 rounded-2xl bg-gray-900/50 border border-gray-800 hover:border-cyan-500/30 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
        <Icon size={28} className="text-cyan-500" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-400 mb-6">{description}</p>
      <ul className="space-y-2">
        {benefits.map((benefit) => (
          <li key={benefit} className="flex items-center gap-2 text-gray-300 text-sm">
            <CheckCircle size={16} className="text-cyan-500" />
            {benefit}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Services — Website Building, AI Agents, Microsoft 365 & IT Consulting in Suffolk"
        description="Explore our services: custom website building, AI agents & automation, AI receptionist, Microsoft 365 business email, and IT consulting. Based in Lowestoft, serving clients nationwide."
        path="/services"
      />
      {/* Hero Section */}
      <section className="py-20 lg:py-32 hero-gradient relative overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Our Services
            </span>
            <h1 
              data-testid="services-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
            >
              End-to-End <span className="gradient-text">IT Solutions</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              From website development to comprehensive managed services, we provide 
              the technology expertise your business needs to thrive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Website Building */}
      <section id="web-building" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="service-section-web-building"
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-6">
                <Globe size={28} className="text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Website Building
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-lg">
                Custom websites that drive results, from stunning landing pages to complex web applications.
              </p>
              <FeatureList features={webFeatures} />
              <Link to="/contact">
                <Button 
                  data-testid="service-cta-web-building"
                  className="btn-primary text-white font-medium px-6"
                >
                  Get Started <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden border border-gray-800">
                <img 
                  src="https://images.unsplash.com/photo-1547658719-da2b51169166?w=800"
                  alt="Custom website building and web design services by Your-IT-Solutions"
                  className="w-full h-[350px] lg:h-[400px] object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Agents & Automation */}
      <section id="ai-automation" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="service-section-ai-automation"
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-6">
                <Bot size={28} className="text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                AI Agents & Automation
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-lg">
                Harness the power of AI agents and workflow automation to eliminate repetitive tasks, boost productivity, and scale your business operations effortlessly.
              </p>
              <FeatureList features={managedFeatures} />
              <Link to="/contact">
                <Button 
                  data-testid="service-cta-ai-automation"
                  className="btn-primary text-white font-medium px-6"
                >
                  Get Started <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden border border-gray-800">
                <img 
                  src="https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwYXV0b21hdGlvbiUyMHJvYm90JTIwY2lyY3VpdCUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3NzY1OTEyMjV8MA&ixlib=rb-4.1.0&q=85"
                  alt="AI agents and workflow automation solutions for businesses"
                  className="w-full h-[350px] lg:h-[400px] object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Consulting */}
      <section id="consulting" className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="service-section-consulting"
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div className="lg:order-2">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-6">
                <Users size={28} className="text-white" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                IT Consulting
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-lg">
                Strategic guidance to align technology initiatives with your business objectives.
              </p>
              <FeatureList features={consultingFeatures} />
              <Link to="/contact">
                <Button 
                  data-testid="service-cta-consulting"
                  className="btn-primary text-white font-medium px-6"
                >
                  Get Started <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="lg:order-1">
              <div className="rounded-2xl overflow-hidden border border-gray-800">
                <img 
                  src="https://images.unsplash.com/photo-1755282464684-6568f7f76b5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxJVCUyMHN0cmF0ZWd5JTIwY29uc3VsdGluZyUyMHRlY2hub2xvZ3klMjBwbGFubmluZyUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3NzY2ODM1MTR8MA&ixlib=rb-4.1.0&q=85"
                  alt="IT consulting and technology strategy services"
                  className="w-full h-[350px] lg:h-[400px] object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Deployment Options */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Deployment Options
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Choose Your Infrastructure
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We support all deployment models to match your business requirements and compliance needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              data-testid="deployment-option-0"
            >
              <DeploymentCard
                Icon={Building2}
                title="On-Premise"
                description="Full control with hardware and software deployed at your location."
                benefits={["Complete data control", "Customizable infrastructure", "No internet dependency"]}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="deployment-option-1"
            >
              <DeploymentCard
                Icon={Layers}
                title="Hybrid"
                description="The best of both worlds - combine on-premise with cloud flexibility."
                benefits={["Flexible workloads", "Cost optimization", "Scalable as needed"]}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="deployment-option-2"
            >
              <DeploymentCard
                Icon={Cloud}
                title="Cloud"
                description="Fully managed cloud infrastructure for maximum scalability."
                benefits={["Instant scalability", "Pay-as-you-go", "Global availability"]}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Not Sure What You Need?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation and our experts will help you identify the 
              right solutions for your business.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="services-cta-consult"
                size="lg"
                className="btn-primary text-white font-medium px-8 h-12"
              >
                Schedule Consultation <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
